'use client';

/*
 * This provider contains data collected in SSR, is like an initial state. It can be data from handshake,
 * initial parameters from url, calculated parameters based in url params, ...
 */
import React, { type ReactNode, useCallback, useState } from 'react';
import { URLParameters } from '@/app/common/urlParameters';
import { updateUrl } from '@/app/common/urlParametersUtils';
import { useSearchPageData } from '../search/searchPageDataProvider';
import { adaptSearchPageHotelData, type HotelStaticData } from './common/staticHotelDataAdapter';
import { useTranslation } from '../translations/Translation';

export type InitialParams = {
  atol: boolean;
  deferredPaymentUrl?: string;
  env?: string;
  hdpSearchId: string;
  internalHotelId: number;
  pageType: Pages;
  partnerUrl: string;
  priceType: PriceType;
  productDetailKey: string;
  taxesIncludedInPrice: boolean;
  tshUrl: string;
  // initial transportId from url. It is important to keep this initial value to be used in create pricing endpoint.
  // The pricingId does not change when the transport is changed, but the url param is changed to build the deeplink.
  // If we use the current url param in the pricing endpoint, we will force new not necessary pricing id's every time
  // the transport is changed
  transportId?: string;
  // In the case the SHAPI returns not availability and there is a transportId from initial url, it will be removed from
  // the url and set to undefined to try a new pricing with default transport
  resetTransportId?: () => void;
  // After using the ratePriceContext once, we need to remove it to not use it in the next pricings (Price calendar, dates change, ...)
  resetPriceContext?: () => void;
  setPriceContext?: (priceContext: string, priceContextSignature: string, rates?: ApexCalendarRate[]) => void;
  staticHotelFallbackData?: HotelStaticData;
  actionIdSeed: string;
  selectedMealPlanCode?: string;
  selectedMealPlanId?: string;
  flightClickId?: string;
  elegibleBrandedFares?: boolean;
  selectedBrandedFareId?: string;
  defaultTransportId?: string;
  isHotelStaticPage: boolean;
  rateApex: boolean;
  ratePriceContext?: string;
  ratePriceContextSignature?: string;
  ratePriceRates?: ApexCalendarRate[];
  // true once the apex calendar fetch has settled (success or failure); always true in non-EVO layout
  isApexCalendarReady: boolean;
  setApexCalendarReady?: () => void;
};

export const InitialParamsContext = React.createContext<InitialParams | undefined>(undefined);

export default function InitialParamsProvider(props: { children: ReactNode } & InitialParams) {
  const { children, ...initialParams } = props;
  const [transportId, setTransportId] = useState(initialParams.transportId);
  const [ratePriceContext, setRatePriceContext] = useState(initialParams.ratePriceContext);
  const [ratePriceContextSignature, setRatePriceContextSignature] = useState(initialParams.ratePriceContextSignature);
  const [ratePriceRates, setRatePriceRates] = useState(initialParams.ratePriceRates);
  const [isApexCalendarReady, setIsApexCalendarReady] = useState(initialParams.isApexCalendarReady);
  const setApexCalendarReady = useCallback(() => setIsApexCalendarReady(true), []);
  const { t } = useTranslation();
  const searchPageData = useSearchPageData();
  const fallbackData = adaptSearchPageHotelData(searchPageData, initialParams.internalHotelId, t);

  const resetTransportId = useCallback(() => {
    updateUrl({
      queryParamsToRemove: [URLParameters.TRANSPORT_ID],
    });
    setTransportId(undefined);
  }, []);

  const resetPriceContext = useCallback(() => {
    setRatePriceContext(undefined);
    setRatePriceContextSignature(undefined);
    setRatePriceRates(undefined);
  }, []);

  const setPriceContext = useCallback(
    (priceContext: string, priceContextSignature: string, rates?: ApexCalendarRate[]) => {
      setRatePriceContext(priceContext);
      setRatePriceContextSignature(priceContextSignature);
      setRatePriceRates(rates);
    },
    []
  );

  initialParams.staticHotelFallbackData = fallbackData || undefined;
  initialParams.transportId = transportId;
  initialParams.resetTransportId = resetTransportId;
  initialParams.resetPriceContext = resetPriceContext;
  initialParams.setPriceContext = setPriceContext;
  initialParams.ratePriceContext = ratePriceContext;
  initialParams.ratePriceContextSignature = ratePriceContextSignature;
  initialParams.ratePriceRates = ratePriceRates;
  initialParams.isApexCalendarReady = isApexCalendarReady;
  initialParams.setApexCalendarReady = setApexCalendarReady;

  return <InitialParamsContext.Provider value={initialParams}>{children}</InitialParamsContext.Provider>;
}

export function useInitialParams() {
  const context = React.useContext(InitialParamsContext);

  if (context === undefined) {
    throw new Error('useInitialParams must be within a InitialParamsProvider');
  }

  return context;
}
